import { useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiFetch, markReconcileHandledByList, reconcileRequiredCount } from './client'

// --- Types ---

export interface ProductTarget {
  id: number
  ad_group_id: number
  amazon_target_id: string
  expression: string
  resolved_expression: string | null
  bid: string | null
  state: 'enabled' | 'paused' | 'archived'
  ad_group_name: string
  campaign_id: number
  campaign_name: string
  ad_type: 'sp' | 'sd'
  impressions: number | null
  clicks: number | null
  spend: number | null
  sales: number | null
  orders: number | null
  acos: number | null
  roas: number | null
  updated_at: string
}

export interface NegativeProductTarget {
  id: number
  campaign_id: number | null
  ad_group_id: number | null
  amazon_negative_target_id: string | null
  expression: string
  state: 'enabled' | 'paused' | 'archived'
  created_at: string
}

export interface TargetFilters {
  limit: number
  offset: number
  search?: string
  expressionType?: 'asin' | 'category' | 'brand' | 'refinement'
  state?: string
  campaignId?: number
  adGroupId?: number
  adType?: 'sp' | 'sd'
  dateFrom?: string
  dateTo?: string
  sortBy: string
  sortDir: 'asc' | 'desc'
}

export interface NegativeTargetFilters {
  limit: number
  offset: number
  search?: string
  campaignId?: number
  adGroupId?: number
}

function toQueryString(filters: object): string {
  const sp = new URLSearchParams()
  Object.entries(filters as Record<string, unknown>).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') sp.set(k, String(v))
  })
  return sp.toString()
}

// --- Positive Product Targets ---

export function useProductTargets(filters: TargetFilters) {
  return useQuery({
    queryKey: ['productTargets', filters],
    queryFn: () =>
      apiFetch<{ productTargets: ProductTarget[]; total: number; limit: number; offset: number }>(
        `/targets/?${toQueryString(filters)}`,
      ),
  })
}

export function useUpdateTargetBid() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, bid }: { id: number; bid: string }) =>
      apiFetch(`/targets/${id}/`, { method: 'PUT', body: JSON.stringify({ bid }) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['productTargets'] })
    },
  })
}

export function useBatchTargetState() {
  const qc = useQueryClient()
  // Scheibe S (REST-O): Verdrahtung wortgleich aus `campaigns.ts` uebernommen.
  // Der Ref statt eines Cleanup-Setters, weil `main.tsx` unter `StrictMode` rendert
  // und React Effekte im Dev-Build doppelt ausfuehrt (mount → cleanup → mount).
  const isMounted = useRef(false)
  useEffect(() => {
    isMounted.current = true
    return () => { isMounted.current = false }
  }, [])

  return useMutation({
    mutationFn: async ({ ids, state }: { ids: number[]; state: string }) => {
      try {
        return await apiFetch(`/targets/batch-state/`, { method: 'POST', body: JSON.stringify({ ids, state }) })
      } catch (err) {
        // Muss HIER stehen und nicht in `onError`: der globale MutationCache-Handler
        // (main.tsx) laeuft VOR jedem `onError`. `TargetsList` zeigt den Fall selbst an —
        // ABER nur, wenn es sie noch gibt. Ist der Operator waehrend des Writes
        // weggeblaettert, bleibt `toasted` unberuehrt und er bekommt den Toast. Ein
        // verschluckter Hinweis waere schlimmer als ein doppelter.
        if (isMounted.current) markReconcileHandledByList(err)
        throw err
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['productTargets'] })
    },
    onError: (err) => {
      // Ein 502 aus `ackOrReconcile` ist ein TEILergebnis: die erfolgreichen Eintraege
      // sind geschrieben. Ohne Invalidierung zeigte die Liste weiter den alten Stand.
      if (reconcileRequiredCount(err) > 0) qc.invalidateQueries({ queryKey: ['productTargets'] })
    },
  })
}

export function useDeleteTarget() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => apiFetch(`/targets/${id}/`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['productTargets'] })
    },
  })
}

export function useCreateTarget() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      adGroupId,
      expression,
      bid,
      state,
    }: {
      adGroupId: number
      expression: string
      bid: string
      state: 'enabled' | 'paused'
    }) =>
      apiFetch(`/ad-groups/${adGroupId}/targets/`, {
        method: 'POST',
        body: JSON.stringify({ expression, bid, state }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['productTargets'] })
    },
  })
}

// --- Negative Product Targets ---

export function useNegativeProductTargets(filters: NegativeTargetFilters) {
  return useQuery({
    queryKey: ['negativeProductTargets', filters],
    queryFn: () =>
      apiFetch<{
        negativeProductTargets: NegativeProductTarget[]
        total: number
        limit: number
        offset: number
      }>(`/negative-targets/?${toQueryString(filters)}`),
  })
}

export function useCreateNegativeProductTarget() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { campaignId?: number; adGroupId?: number; expression: string }) =>
      apiFetch(`/negative-targets/`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['negativeProductTargets'] })
    },
  })
}

export function useDeleteNegativeProductTarget() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => apiFetch(`/negative-targets/${id}/`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['negativeProductTargets'] })
    },
  })
}
